import { openNotificationDrawer } from "@components/utils/NotificationDrawer/utils";
import { useAnalyticsTracking } from "@configs/analytics";
import { NotificationInAppSource } from "@configs/analytics/events/types";
import { envConfig } from "@configs/env";
import { NotificationDrawerEnabledStatus } from "@constants/misc";
import { NOTIFICATION_DRAWER_ROUTES } from "@constants/paths";
import useAuthSession from "@hooks/useAuthSession";
import { NotificationPermissionState } from "@types";
import { getDevFlags } from "@utils/dev-flags";
import { getNotificationPermission, isNotificationSupported } from "@utils/notification";
import { getRuntimeContext } from "@utils/pwa";
import { SW_MEMBER_ID_STORAGE_KEY, delay, getPersistedDueAt, persistDueAt, updatePushSubscription } from "kz-ui-sdk";
import moment from "moment";
import { useCallback, useMemo, useRef } from "react";
import { matchPath } from "react-router-dom";

const SEVEN_DAYS_IN_SECONDS = 7 * 24 * 60 * 60;
const NOTIFICATION_DRAWER_REMINDER_KEY = "notification-drawer-reminder";

export const PWA_NOTIFICATION_SNOOZE_PREFIX = `pk-${NOTIFICATION_DRAWER_REMINDER_KEY}:pwa:`;

const NOTIFICATION_DRAWER_DELAY = 100;

export const useNotificationHandler = () => {
  const { self } = useAuthSession();

  const memberIdRef = useRef(self?.member?.id);
  memberIdRef.current = self?.member?.id;

  const reminderKey = useMemo(() => {
    const memberId = self?.member?.id;
    const context = getRuntimeContext();
    return memberId
      ? `${NOTIFICATION_DRAWER_REMINDER_KEY}:${context}:${memberId}`
      : `${NOTIFICATION_DRAWER_REMINDER_KEY}:${context}:guest`;
  }, [self?.member?.id]);

  const { trackNotificationSubscriptionStatus, trackNotificationBrowserPromptShown } = useAnalyticsTracking();

  const permission = getNotificationPermission();

  const notificationDrawerEnabledStatus = useMemo(() => {
    const devFlagValue = getDevFlags()?.notificationDrawerEnabled;
    return devFlagValue === false ? NotificationDrawerEnabledStatus.DISABLED : NotificationDrawerEnabledStatus.ENABLED;
  }, []);

  const shouldPrompt = useMemo(() => {
    return (
      notificationDrawerEnabledStatus === NotificationDrawerEnabledStatus.ENABLED &&
      isNotificationSupported() &&
      permission === NotificationPermissionState.DEFAULT &&
      !!import.meta.env.VITE_PUSH_NOTIFICATION_URI &&
      !!self?.member?.id
    );
  }, [permission, notificationDrawerEnabledStatus, self?.member?.id]);

  const snooze = useCallback(() => {
    persistDueAt(reminderKey, moment().add(SEVEN_DAYS_IN_SECONDS, "seconds"));
  }, [reminderKey]);

  // Ask for permission and subscribe to push notifications
  const requestPermissionAndSubscribe = useCallback(
    async ({
      alwaysSubscribe = false,
      source,
    }: { alwaysSubscribe?: boolean; source?: NotificationInAppSource } = {}) => {
      if (!isNotificationSupported()) return NotificationPermissionState.DENIED;
      const currentPermission = getNotificationPermission();
      let result: NotificationPermissionState = currentPermission;
      let didPrompt = false;

      if (currentPermission === NotificationPermissionState.DEFAULT) {
        didPrompt = true;
        try {
          trackNotificationBrowserPromptShown(source);
          result = (await Notification.requestPermission()) as NotificationPermissionState;
        } catch (_) {
          result = NotificationPermissionState.DENIED;
        }
      }

      const memberId = memberIdRef.current;
      if (result === NotificationPermissionState.GRANTED && memberId) {
        try {
          const registeredMemberId = sessionStorage.getItem(SW_MEMBER_ID_STORAGE_KEY);
          if (!registeredMemberId || registeredMemberId !== memberId || alwaysSubscribe)
            await updatePushSubscription({
              memberId,
              accountId: envConfig.accountId,
              notificationURI: import.meta.env.VITE_PUSH_NOTIFICATION_URI,
              notificationTestParam: import.meta.env.VITE_TEST_NOTIFICATION_PARAM,
              publicUrl: import.meta.env.VITE_PUBLIC_URL,
            });
        } catch (_) {
          // ignore
        }
      }

      if (didPrompt) {
        trackNotificationSubscriptionStatus(result, source);
      }

      return result;
    },
    [trackNotificationSubscriptionStatus, trackNotificationBrowserPromptShown],
  );

  // Check(but not ask) if permission is granted and subscribe to push notifications
  const checkPermissionAndSubscribe = useCallback(async () => {
    const permission = getNotificationPermission();
    if (permission === NotificationPermissionState.GRANTED && memberIdRef.current) {
      await requestPermissionAndSubscribe();
    }
  }, [requestPermissionAndSubscribe]);

  const openIfEligible = useCallback(async () => {
    if (!shouldPrompt) return;
    // Delay to avoid race condition with the route change
    await delay(NOTIFICATION_DRAWER_DELAY);
    const isNotificationDrawerAllowedRoute = NOTIFICATION_DRAWER_ROUTES.some((route) =>
      matchPath(route, window.location.pathname),
    );
    if (!isNotificationDrawerAllowedRoute) return;
    const nextDisplayAt = getPersistedDueAt(reminderKey);

    const isDue = nextDisplayAt ? moment().isAfter(nextDisplayAt) : true;
    if (!isDue) return;
    openNotificationDrawer();
  }, [shouldPrompt, reminderKey]);

  return {
    shouldPrompt,
    snooze,
    requestPermissionAndSubscribe,
    checkPermissionAndSubscribe,
    openIfEligible,
    notificationDrawerEnabledStatus,
  };
};
